import { MapPin, RotateCcw } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import GenericModal from 'src/components/generic-modal';
export default function EditAllocatedRevenue({
  data,
  order,
  legs = [],
  totalRevenue = 0,
  orderUuid,
  openAllocated,
  handleCloseAllocated,
  onSave,
}) {
  const safeLegs = Array.isArray(legs) ? legs : [];

  const isLegLineHaul = (leg) => {
    // Check all possible field names (camelCase, lowercase, snake_case)
    // and handle various truthy values (boolean, string "true", number 1)
    const value =
      leg?.isLineHaul ??
      leg?.isLinehaul ??
      leg?.lineHaul ??
      leg?.linehaul ??
      leg?.is_line_haul ??
      leg?.line_haul;
    if (value === true || value === 'true' || value === 1 || value === '1') {
      return true;
    }
    return !!value;
  };
  const safeTotalRevenue = Number.isFinite(Number(totalRevenue))
    ? Number(totalRevenue)
    : 0;

  const [percents, setPercents] = useState([]);

  const toBasisPoints = (value) => {
    const v = Number(value);
    if (!Number.isFinite(v)) return 0;
    return Math.round(v * 100);
  };

  const fromBasisPoints = (bp) => {
    const v = Number(bp);
    if (!Number.isFinite(v)) return 0;
    return v / 100;
  };

  function clamp(value, min, max) {
    const v = Number(value);
    if (!Number.isFinite(v)) return min;
    return Math.max(min, Math.min(max, v));
  }

  const roundPercent = (value) => {
    const v = Number(value);
    if (!Number.isFinite(v)) return 0;
    if (Math.abs(v - 100) < 1e-9) return 100;
    return Math.round(v);
  };

  const normalizeDefaultPercents = (values) => {
    if (!Array.isArray(values) || !values.length) return [];
    if (values.length === 1) return [100];

    const nextBp = values.map((p) => clamp(toBasisPoints(p), 0, 10000));
    const sumBp = nextBp.reduce((acc, v) => acc + v, 0);
    const diffBp = 10000 - sumBp;

    if (diffBp === 0) return nextBp.map(fromBasisPoints);

    let maxIdx = 0;
    for (let i = 1; i < nextBp.length; i += 1) {
      if (nextBp[i] > nextBp[maxIdx]) maxIdx = i;
    }
    nextBp[maxIdx] = clamp(nextBp[maxIdx] + diffBp, 0, 10000);
    return nextBp.map(fromBasisPoints);
  };

  const defaultPercents = useMemo(() => {
    if (!safeLegs.length) return [];

    // Force line haul legs to 0% in defaults
    const lineHaulIndices = new Set();
    safeLegs.forEach((leg, idx) => {
      if (isLegLineHaul(leg)) {
        lineHaulIndices.add(idx);
      }
    });

    const total = safeTotalRevenue;
    const fromExisting =
      total > 0 &&
      safeLegs.some(
        (l) => Number.isFinite(Number(l?.revenue)) && !isLegLineHaul(l)
      );

    if (fromExisting) {
      const raw = safeLegs.map((l, idx) => {
        // Line haul legs always get 0%
        if (lineHaulIndices.has(idx)) return 0;
        const v = Number(l?.revenue);
        return Number.isFinite(v) ? (v / total) * 100 : 0;
      });

      const sum = raw.reduce((a, b) => a + b, 0);
      if (sum > 0) {
        const scale = 100 / sum;
        const normalized = normalizeDefaultPercents(raw.map((p) => p * scale));
        // Ensure line haul legs stay at 0%
        return normalized.map((p, idx) => (lineHaulIndices.has(idx) ? 0 : p));
      }
    }

    if (safeLegs.length === 1) {
      return [isLegLineHaul(safeLegs[0]) ? 0 : 100];
    }
    const defaults = normalizeDefaultPercents(
      safeLegs.map((_, idx) => (idx === 0 ? 100 : 0))
    );
    // Ensure line haul legs stay at 0%
    return defaults.map((p, idx) => (lineHaulIndices.has(idx) ? 0 : p));
  }, [safeLegs, safeTotalRevenue]);

  useEffect(() => {
    if (!openAllocated) return;

    // Ensure line haul legs are always 0 in percents
    const percentsWithLineHaulZero = defaultPercents.map((p, idx) =>
      isLegLineHaul(safeLegs[idx]) ? 0 : p
    );
    setPercents(percentsWithLineHaulZero);
  }, [openAllocated, defaultPercents, safeLegs.length]);

  const percentTotal = useMemo(() => {
    const sumBp = (Array.isArray(percents) ? percents : []).reduce(
      (acc, v) => acc + toBasisPoints(v),
      0
    );
    return fromBasisPoints(sumBp);
  }, [percents]);

  const percentTotalBasisPoints = useMemo(() => {
    return (Array.isArray(percents) ? percents : []).reduce(
      (acc, v) => acc + toBasisPoints(v),
      0
    );
  }, [percents]);

  const isPercentTotalValid = useMemo(() => {
    if (!safeLegs.length) return false;
    if (safeLegs.length === 1) return true;
    return percentTotalBasisPoints === 10000;
  }, [percentTotalBasisPoints, safeLegs.length]);

  // Rebalance basis points so that:
  // - Edited leg keeps its new value
  // - All other legs (excluding line haul) are scaled proportionally so total is exactly 10000 bp (100%)
  // - Line haul legs are always 0 and excluded from rebalancing
  // - Works for both increases and decreases of the edited leg
  const rebalanceBasisPoints = (currentBp, editedIndex) => {
    const TOTAL_BP = 10000;
    const nextBp = [...currentBp];

    // Exclude line haul legs from rebalancing - they stay at 0
    const lineHaulIndices = new Set();
    safeLegs.forEach((leg, idx) => {
      if (isLegLineHaul(leg)) {
        lineHaulIndices.add(idx);
        nextBp[idx] = 0; // Ensure line haul legs are 0
      }
    });

    // If edited leg is line haul, don't rebalance
    if (lineHaulIndices.has(editedIndex)) {
      return nextBp;
    }

    // Calculate remaining bp excluding line haul legs
    const nonLineHaulBp = nextBp.reduce(
      (sum, bp, idx) => sum + (lineHaulIndices.has(idx) ? 0 : bp),
      0
    );
    const editedBp = nextBp[editedIndex];
    let remainingBp = TOTAL_BP - editedBp;

    // If remaining is negative, force edited to 100% and others to 0.
    if (remainingBp <= 0) {
      nextBp[editedIndex] = TOTAL_BP;
      for (let i = 0; i < nextBp.length; i += 1) {
        if (i !== editedIndex && !lineHaulIndices.has(i)) {
          nextBp[i] = 0;
        }
      }
      return nextBp;
    }

    // Collect other indices (excluding line haul and edited) and their current bp.
    const otherIndices = [];
    let sumOtherBp = 0;
    for (let i = 0; i < nextBp.length; i += 1) {
      if (i === editedIndex || lineHaulIndices.has(i)) continue;
      otherIndices.push(i);
      sumOtherBp += Math.max(0, nextBp[i]);
    }

    if (otherIndices.length === 0) {
      // Should not happen (we handle 1-leg case above), but be safe.
      nextBp[editedIndex] = TOTAL_BP;
      return nextBp;
    }

    if (sumOtherBp === 0) {
      // All other legs are currently 0 => distribute remaining equally.
      const baseShare = Math.floor(remainingBp / otherIndices.length);
      let leftover = remainingBp - baseShare * otherIndices.length;

      for (const i of otherIndices) {
        let share = baseShare;
        if (leftover > 0) {
          share += 1;
          leftover -= 1;
        }
        nextBp[i] = share;
      }
      return nextBp;
    }

    // Proportional redistribution based on current values.
    const exactShares = [];
    const baseShares = [];
    const fractions = [];

    for (const i of otherIndices) {
      const exact = (remainingBp * nextBp[i]) / sumOtherBp;
      const base = Math.floor(exact);
      exactShares.push(exact);
      baseShares.push(base);
      fractions.push({ index: i, frac: exact - base });
    }

    // Apply base shares.
    let sumBase = 0;
    for (let k = 0; k < otherIndices.length; k += 1) {
      const legIndex = otherIndices[k];
      nextBp[legIndex] = baseShares[k];
      sumBase += baseShares[k];
    }

    // Distribute leftover basis points by largest fractional part.
    let remainder = remainingBp - sumBase;
    fractions.sort((a, b) => b.frac - a.frac);
    let idx = 0;
    while (remainder > 0 && idx < fractions.length) {
      const legIndex = fractions[idx].index;
      nextBp[legIndex] += 1;
      remainder -= 1;
      idx += 1;
    }

    return nextBp;
  };

  const handlePercentChange = (index, rawValue) => {
    if (!safeLegs.length) return;

    // Don't allow changes for line haul legs
    const leg = safeLegs[index];
    if (isLegLineHaul(leg)) {
      return;
    }

    const parsed = rawValue === '' ? 0 : Number(rawValue);
    const desired = Number.isFinite(parsed) ? roundPercent(parsed) : 0;

    if (safeLegs.length === 1) {
      setPercents([isLegLineHaul(safeLegs[0]) ? 0 : 100]);
      return;
    }

    const nextBp = (Array.isArray(percents) ? percents : []).map((p, i) => {
      // Keep line haul legs at 0
      if (isLegLineHaul(safeLegs[i])) return 0;
      return clamp(toBasisPoints(p), 0, 10000);
    });
    while (nextBp.length < safeLegs.length) {
      const idx = nextBp.length;
      nextBp.push(isLegLineHaul(safeLegs[idx]) ? 0 : 0);
    }

    nextBp[index] = clamp(toBasisPoints(desired), 0, 10000);
    const rebalancedBp = rebalanceBasisPoints(nextBp, index);

    // Ensure line haul legs stay at 0 after rebalancing
    const finalBp = rebalancedBp.map((bp, i) =>
      isLegLineHaul(safeLegs[i]) ? 0 : bp
    );

    setPercents(finalBp.map(fromBasisPoints));
  };

  const computedNewRevenues = useMemo(() => {
    const totalCents = Math.round(safeTotalRevenue * 100);
    if (!safeLegs.length) return [];
    if (totalCents <= 0) return safeLegs.map(() => 0);

    const safePercents = Array.isArray(percents) ? percents : [];
    const percentBps = safeLegs.map((leg, i) =>
      // Force line haul legs to 0% when computing new revenues
      isLegLineHaul(leg)
        ? 0
        : clamp(toBasisPoints(safePercents[i] || 0), 0, 10000)
    );
    if (safeLegs.length === 1) {
      // If the only leg is line haul, its new revenue should be 0
      return [isLegLineHaul(safeLegs[0]) ? 0 : totalCents / 100];
    }

    if (percentTotalBasisPoints === 10000) {
      const exact = safeLegs.map(
        (_, i) => (totalCents * percentBps[i]) / 10000
      );
      const base = exact.map((v) => Math.floor(v));
      const fractions = exact.map((v, i) => ({ i, frac: v - base[i] }));
      const sumBase = base.reduce((acc, v) => acc + v, 0);
      let remainder = totalCents - sumBase;

      fractions.sort((a, b) => b.frac - a.frac);
      for (let k = 0; k < fractions.length && remainder > 0; k += 1) {
        base[fractions[k].i] += 1;
        remainder -= 1;
      }

      return base.map((c, i) =>
        // Line haul legs must have 0 new revenue
        isLegLineHaul(safeLegs[i]) ? 0 : c / 100
      );
    }

    const centsByLeg = safeLegs.map((leg, i) =>
      isLegLineHaul(leg) ? 0 : Math.round((totalCents * percentBps[i]) / 10000)
    );
    return centsByLeg.map((c, i) =>
      // Ensure line haul legs always show 0 new revenue
      isLegLineHaul(safeLegs[i]) ? 0 : c / 100
    );
  }, [percentTotalBasisPoints, percents, safeLegs, safeTotalRevenue]);

  const handleRevert = () => {
    setPercents(defaultPercents);
  };

  const handlePrimaryAction = async () => {
    try {
      // Debug: Log leg data to see isLineHaul values
      console.log(
        'Legs data for allocated revenue:',
        safeLegs.map((leg) => ({
          uuid: leg?.uuid,
          legNumber: leg?.legNumber,
          isLineHaul: leg?.isLineHaul,
          isLinehaul: leg?.isLinehaul,
          lineHaul: leg?.lineHaul,
          linehaul: leg?.linehaul,
          isLegLineHaulResult: isLegLineHaul(leg),
        }))
      );

      const payload = safeLegs.map((leg, index) => {
        // Line haul legs must always be sent with 0 percent and 0 revenue
        if (isLegLineHaul(leg)) {
          console.log(
            `Leg ${leg?.legNumber} is line haul, setting revenue to 0`
          );
          return {
            uuid: leg?.uuid,
            percent: 0,
            revenue: 0,
          };
        }

        return {
          uuid: leg?.uuid,
          percent: roundPercent(Number(percents?.[index] || 0)),
          revenue: Number(computedNewRevenues?.[index] || 0),
        };
      });

      console.log('Final payload:', payload);

      if (onSave) {
        await onSave(payload);
      } else {
        console.log('Save payload:', payload);
      }

      handleCloseAllocated();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <GenericModal
      open={openAllocated}
      onClose={handleCloseAllocated}
      title={`Edit Allocated Revenue: ${
        order?.orderNumber ? `Order - ${order.orderNumber}` : ''
      }`}
      primaryButtonText={'Save'}
      secondaryButtonText="Cancel"
      onPrimaryAction={handlePrimaryAction}
      primaryButtonProps={{ disabled: !isPercentTotalValid }}
      maxWidth="md"
      fullWidth={true}
      desktopWidth="1200px"
      buttonsInHeader={false}
    >
      <div className="mx-auto p-0 font-sans">
        <div className="border rounded-lg overflow-hidden">
          {/* Header Row */}
          <div className="grid grid-cols-8 text-xs text-gray-500 bg-gray-50 p-2">
            <div>STATUS</div>
            <div>LEG</div>
            <div>TYPE</div>
            <div>ORIGIN</div>
            <div>DESTINATION</div>
            <div className=" text-center whitespace-nowrap"> MANIFEST</div>
            <div className=" text-left whitespace-nowrap">
              ALLOCATED REV CURRENT
            </div>
            <div className=" text-right whitespace-nowrap">
              ALLOCATED REV NEW
            </div>
          </div>

          {safeLegs.map((leg, index) => (
            <div
              key={leg?.uuid || `leg-${index}`}
              className={`grid grid-cols-8 items-center p-2 border-t text-sm ${
                isLegLineHaul(leg) ? 'bg-orange-50' : ''
              }`}
            >
              <div>
                <span className="bg-gray-100 px-2 py-1 rounded text-gray-700 font-medium">
                  {leg?.status || 'N/A'}
                </span>
              </div>
              <div>
                <span className="font-medium">{leg?.legNumber || '—'}</span>
              </div>
              <div className="flex items-center flex-wrap gap-1">
                <span className="text-gray-700">{leg?.type || '—'}</span>
                {isLegLineHaul(leg) && (
                  <span className="bg-orange-100 text-orange-700 text-xs px-1 rounded">
                    Line Haul
                  </span>
                )}
              </div>
              <div className="flex items-center">
                <MapPin className="h-4 w-4 text-gray-400 mr-1" />
                <span className="text-gray-700">
                  {leg?.origin?.city || leg?.origin?.name || '—'}
                  {leg?.origin?.state ? `, ${leg.origin.state}` : ''}
                </span>
              </div>
              <div className="flex items-center">
                <MapPin className="h-4 w-4 text-gray-400 mr-1" />
                <span className="text-gray-700">
                  {leg?.destination?.city || leg?.destination?.name || '—'}
                  {leg?.destination?.state ? `, ${leg.destination.state}` : ''}
                </span>
              </div>
              <div className="text-center">
                {leg?.manifestStop?.manifest?.manifestName ||
                  leg?.manifestStop?.manifest?.manifestNumber ||
                  ''}
              </div>
              <div className="text-right font-medium mr-10">
                ${Number(leg?.revenue || 0).toFixed(2)}
              </div>
              <div className="text-right font-medium">
                <div className="flex items-center justify-end">
                  <input
                    type="number"
                    value={
                      isLegLineHaul(leg)
                        ? 0
                        : Math.round(Number(percents?.[index] ?? 0))
                    }
                    onChange={(e) => handlePercentChange(index, e.target.value)}
                    className={`w-[70px] text-right border rounded px-2 py-1 ${
                      isLegLineHaul(leg)
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : ''
                    }`}
                    min="0"
                    step="1"
                    disabled={isLegLineHaul(leg)}
                    readOnly={isLegLineHaul(leg)}
                  />
                  <div className="w-[90px] text-right">
                    ${Number(computedNewRevenues?.[index] || 0).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Footer Row */}
          <div className="grid grid-cols-8 items-center p-2 bg-gray-50 border-t">
            <div className="col-span-2">
              <button
                onClick={handleRevert}
                className="flex items-center text-blue-500 text-sm"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                REVERT TO DEFAULT
              </button>
            </div>
            <div className="col-span-4"></div>
            <div className="text-right font-medium mr-10">
              $
              {safeLegs
                .reduce((acc, l) => acc + (Number(l?.revenue) || 0), 0)
                .toFixed(2)}
            </div>
            <div className="text-right">
              <div className="font-medium">
                $
                {computedNewRevenues
                  .reduce((acc, v) => acc + (Number(v) || 0), 0)
                  .toFixed(2)}
              </div>
              <div className="text-xs text-gray-500">
                {safeLegs.length ? `${Math.round(percentTotal)}%` : '0%'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </GenericModal>
  );
}
